import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo
} from 'react';

import {
  AdmissionType,
  FeeStructureContextProps,
  PaymentOption
} from '../types';
import { usePaymentSchemeApiHook } from '../hook/usePaymenSchemeApiHook';
import { academicYears } from '../../../constants/academicYears';
import { SnackbarSeverityType } from '../../../enums';
import paymentSchemesApis from '../../../apis/payments/paymentSchemes';

const FeeStructureContext = createContext<FeeStructureContextProps | undefined>(
  undefined
);

export const FeeStructureProvider: React.FC<{
  children: React.ReactNode;
  loggedInUser: any;
}> = ({ children, loggedInUser }) => {
  const defaultAcademicYear = academicYears.find((year) => year.isDefault);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState<string>(
    defaultAcademicYear?.value || ''
  );
  const [selectedGrade, setSelectedGrade] = useState<string>('');
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [admissionType, setAdmissionType] = useState<AdmissionType>('rollover');

  const [isPaymentOptionModalOpen, setIsPaymentOptionModalOpen] = useState<
    'newPayment' | 'clonePayment' | null
  >(null);
  const [isPaymentSchemeDetail, setIsPaymentSchemeDetail] = useState<{
    data: PaymentOption | null;
    isOpen: boolean;
  }>({ data: null, isOpen: false });

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const {
    remarksUpdating,
    paymentSchemeData,
    setPaymentSchemeData,
    paymentSchemeRemarksUpdate,
    handlePaymentSchemeDelete,
    handleAddPaymentScheme,
    handlePaymentSchemeClone,
    handlePaymentSchemeDetailUpdate
  } = usePaymentSchemeApiHook({
    admissionType,
    school: loggedInUser.school,
    academicYear: selectedAcademicYear,
    grade: selectedGrade,
    showToast
  });

  const handleSearch = useCallback(async () => {
    setIsSearching(true);
    setPaymentSchemeData(null);
    try {
      const response = await paymentSchemesApis.fetchPaymentScheme({
        school: loggedInUser.school,
        academicYear: selectedAcademicYear,
        grade: selectedGrade
      });
      setPaymentSchemeData(response as any);
      showToast(
        'Successfully fetched payment option scheme',
        SnackbarSeverityType.success
      );
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Error fetching payment option scheme',
        SnackbarSeverityType.error
      );
    } finally {
      setIsSearching(false);
      setHasSearched(true);
    }
  }, [
    selectedAcademicYear,
    selectedGrade,
    loggedInUser.school,
    setPaymentSchemeData
  ]);

  const paymentOptionData = useMemo(() => {
    return admissionType === 'newAdmission'
      ? paymentSchemeData?.newAdmissionPaymentOptions
      : paymentSchemeData?.paymentOptions;
  }, [admissionType, paymentSchemeData]);

  const remarks = useMemo(() => {
    return admissionType === 'newAdmission'
      ? paymentSchemeData?.remarks?.newAdmission
      : paymentSchemeData?.remarks?.rollOver;
  }, [admissionType, paymentSchemeData]);

  const academicYearOptions = academicYears.slice(1);

  const handleCloseToast = () => setToastOpen(false);

  const openPaymentSchemeDetail = (data: PaymentOption) =>
    setIsPaymentSchemeDetail({ data, isOpen: true });
  const handlePaymentSchemeDetailClose = () =>
    setIsPaymentSchemeDetail({ data: null, isOpen: false });

  const handleTableRowEdit = openPaymentSchemeDetail;

  const value = {
    loggedInUser,
    selectedAcademicYear,
    setSelectedAcademicYear,
    selectedGrade,
    setSelectedGrade,
    academicYears: academicYearOptions,
    hasSearched,
    setHasSearched,
    isSearching,
    setIsSearching,
    handleSearch,
    admissionType,
    setAdmissionType,
    paymentOptionData,
    handlePaymentSchemeClone,
    handlePaymentSchemeDelete,
    handleAddPaymentScheme,
    remarks,
    paymentSchemeRemarksUpdate,
    remarksUpdating,
    toastOpen,
    toastMessage,
    toastSeverity,
    showToast,
    handleCloseToast,
    isPaymentOptionModalOpen,
    setIsPaymentOptionModalOpen,
    isPaymentSchemeDetail,
    openPaymentSchemeDetail,
    handlePaymentSchemeDetailClose,
    handleTableRowEdit,
    handlePaymentSchemeDetailUpdate
  };

  return (
    <FeeStructureContext.Provider value={value}>
      {children}
    </FeeStructureContext.Provider>
  );
};

export function useFeeStructureContext() {
  const context = useContext(FeeStructureContext);
  if (!context)
    throw new Error(
      'useFeeStructureContext must be used within FeeStructureProvider'
    );
  return context;
}

export default FeeStructureProvider;
